import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { useAbility } from "@casl/vue";
import {
  useCanAccessAdministration,
  useCanAccessOfficesManagement,
  useCanAccessPlatformControl,
} from "@/composables/shared/useSectionPermissions";
import { ORGANIZATION_ROOT_PATH } from "@/composables/shared/useMyOrganization";
import { useInvitePermissions } from "@/composables/models/invite/useInvitePermissions";
import { dashboardDefaultRoute } from "@/composables/models/patient/useDashboardDestinations";
import { useSettingsStore } from "@/stores/settingsStore";

export const NO_LANDING_ROUTE = "/unauthorized";

type TLandingCandidate = {
  route: RouteLocationRaw;
  allowed: boolean;
  /** i18n key, reusing the navbar's labels so the two never drift. */
  label: string;
  icon: string;
};

/** A section the current user can reach, for offering navigation choices. */
export type TReachableSection = {
  route: RouteLocationRaw;
  label: string;
  icon: string;
};

/**
 * Resolves the first application section the current user is actually allowed to see.
 *
 * Sections are ordered to mirror the navbar, so the landing page a user gets is the
 * top-most entry they can still reach. Roles without `READ patient.patient` skip the
 * patient dashboard entirely instead of dead-ending on the Unauthorized page.
 *
 * The dashboard entry resolves through the user's stored default-tab preference, so
 * landing lands them where they asked to be rather than on a fixed patient list.
 */
export function useLandingRoute() {
  const { can } = useAbility();
  const settingsStore = useSettingsStore();

  const canAccessOfficesManagement = useCanAccessOfficesManagement();
  const canAccessAdministration = useCanAccessAdministration();
  const canAccessPlatformControl = useCanAccessPlatformControl();
  const { canReadInvite } = useInvitePermissions();

  const canReadPatients = computed(() => can($permActions.READ, $permSubjects.PATIENT__PATIENT));
  const canReadReferrals = computed(() => can($permActions.READ, $permSubjects.REFERRAL__REFERRAL));

  const patientDashboardRoute = computed(() =>
    dashboardDefaultRoute(settingsStore.dashboardDefaultTab, canReadReferrals.value)
  );

  const candidates = computed<TLandingCandidate[]>(() => [
    {
      route: patientDashboardRoute.value,
      allowed: canReadPatients.value,
      label: "generic.dashboard.title",
      icon: "mdi-account-multiple",
    },
    {
      route: ORGANIZATION_ROOT_PATH,
      allowed: canAccessOfficesManagement.value,
      label: "generic.entity.organization",
      icon: "mdi-hospital-building",
    },
    {
      route: "/administrative",
      allowed: canAccessAdministration.value,
      label: "generic.navbar.paths.administrative",
      icon: "mdi-cog",
    },
    {
      route: "/admin-settings",
      allowed: canAccessPlatformControl.value,
      label: "generic.navbar.menuUser.adminSettings",
      icon: "mdi-application-cog",
    },
    {
      route: "/settings",
      allowed: true,
      label: "generic.entity.settings",
      icon: "mdi-account-cog",
    },
  ]);

  const landingRoute = computed<RouteLocationRaw>(
    () => candidates.value.find((candidate) => candidate.allowed)?.route ?? NO_LANDING_ROUTE
  );

  /**
   * Every section the user can actually open. The error page offers these as
   * alternatives, so a dead end always has at least one way out.
   */
  const reachableSections = computed<TReachableSection[]>(() =>
    candidates.value
      .filter((candidate) => candidate.allowed)
      .map(({ route, label, icon }) => ({ route, label, icon }))
  );

  return {
    landingRoute,
    reachableSections,
    patientDashboardRoute,
    // Re-exported so consumers that gate UI on section access (the navbar) read the
    // same flags this landing decision is made from, instead of assembling their own
    // copy of the set and drifting from it.
    canReadPatients,
    canReadInvite,
    canAccessOfficesManagement,
    canAccessAdministration,
    canAccessPlatformControl,
  };
}
